import { useCallback, useState } from "react";
import { IClientData } from "../../../../models/ClientData";
import { CalendlyBookingDetails, CalendlyCustomQuestionConfig, CalendlyCustomWidget } from "../CalendlyCustomWidget";
import { AsFeaturedOn } from "./AsFeaturedOn";

export const defaultAppointmentType = "portal-general-appointment";
const callNumber = "0808 281 2558";
const callHref = "tel:08082812558";

const appointmentConfigs: Record<string, {
  summaryCopy: string;
  customQuestions: CalendlyCustomQuestionConfig[];
}> = {
  "portal-general-appointment": {
    summaryCopy: "Book a free appointment with one of Pense's Retirement Specialists.",
    customQuestions: [
      {
        question: "Please confirm your phone number",
        position: 0,
        required: true,
        label: "Phone",
        inputType: "tel",
        autoComplete: "tel",
        bookingDetailField: "phone",
      },
    ],
  },
};

function getStringValue(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

export function buildInitialBookingDetails(sourceData: Record<string, any>): CalendlyBookingDetails {
  const firstName = getStringValue(sourceData.firstName);
  const lastName = getStringValue(sourceData.lastName);

  return {
    title: getStringValue(sourceData.title),
    fullName: [firstName, lastName].filter(Boolean).join(" ") || getStringValue(sourceData.fullName),
    firstName,
    lastName,
    email: getStringValue(sourceData.email),
    phone: getStringValue(sourceData.primaryPhone) || getStringValue(sourceData.telephone),
  };
}

export function bookingDetailsToLeadFields(details: CalendlyBookingDetails): Partial<IClientData> {
  return {
    title: details.title,
    firstName: details.firstName,
    lastName: details.lastName,
    email: details.email,
    primaryPhone: details.phone,
  };
}

export interface AdhocBookingPanelProps {
  appointmentType?: string;
  initialDetails: CalendlyBookingDetails;
  /** Saves the lead with this booking; resolves false when the CRM update failed. */
  saveBooking: (contact: Partial<IClientData>, booking: Partial<IClientData>) => Promise<boolean>;
  onDetailsChange?: (details: CalendlyBookingDetails) => void;
  confirmationCtaHref?: string;
  onConfirmationCtaClick?: () => void;
}

export function AdhocBookingPanel({
  appointmentType = defaultAppointmentType,
  initialDetails,
  saveBooking,
  onDetailsChange,
  confirmationCtaHref,
  onConfirmationCtaClick,
}: AdhocBookingPanelProps) {
  const config = appointmentConfigs[appointmentType] ?? appointmentConfigs[defaultAppointmentType];
  const [bookingDetails, setBookingDetails] = useState<CalendlyBookingDetails>(initialDetails);
  const [isBooked, setIsBooked] = useState(false);

  const handleBookingDetailsChange = useCallback((details: CalendlyBookingDetails) => {
    setBookingDetails(details);
    onDetailsChange?.(details);
  }, [onDetailsChange]);

  const handlePostBookingCrmUpdate = useCallback(async (payload: any, details: CalendlyBookingDetails) => {
    const saved = await saveBooking(bookingDetailsToLeadFields(details), {
      calendlyEventJSON: payload?.calendlyEventJson ?? "",
      portalGeneralAppointmentBookedLead: new Date().toISOString(),
    });
    if (!saved) {
      throw new Error("Lead booking update failed.");
    }
  }, [saveBooking]);

  return (
    <section className="pa-v1-booking">
      {!isBooked ? <h1>Let's find a time that works for you</h1> : null}
      <CalendlyCustomWidget
        appointmentType={appointmentType}
        bookingDetails={bookingDetails}
        onBookingDetailsChange={handleBookingDetailsChange}
        onBooked={() => setIsBooked(true)}
        onBookedStateChange={setIsBooked}
        customQuestionConfigs={config.customQuestions}
        enableSmsReminderOptIn
        summaryCopy={config.summaryCopy}
        confirmationTitle="Use our free annuity calculator to get quotes from the whole market"
        confirmationCtaLabel="CONTINUE TO SEE QUOTES"
        confirmationCtaHref={confirmationCtaHref}
        onConfirmationCtaClick={onConfirmationCtaClick}
        onPostBookingCrmUpdate={handlePostBookingCrmUpdate}
      />
      {!isBooked ? (
        <>
          <section className="pa-v1-booking-call-now" aria-labelledby="adhoc-booking-call-now">
            <div>
              <h2 id="adhoc-booking-call-now">Need to talk now?</h2>
              <span>Call free on</span>
              <a href={callHref}>{callNumber}</a>
            </div>
          </section>
          <AsFeaturedOn />
        </>
      ) : null}
    </section>
  );
}
